import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  Image,
  Form,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import {
  getObjectsById,
  objectsListFetch,
  deleteObjects,
} from "pages/AccessManagement/actions";
import { trimAndLimit } from "utils/general";
import ObjectViewModal from "./components/ObjectViewModal/ObjectViewModal";
import { DeleteModal, StatusBadge, TablePagination, TableSearchField } from "CommonComponent";
import AccessRights from "utils/AccessRights";
import ObjectUpdate from "../update/ObjectUpdate";

const toggleStatus = (is_active) => (
  <StatusBadge
    label={is_active === "y" ? "Active" : "Inactive"}
    variant={is_active === "y" ? "active" : "inactive"}
  />
);

const ObjectList = ({ ...rest }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Safe Redux selector with fallback
  const rawObjectsState = useSelector((state) => state.objectsState || {});
  const objectsState = {
    objectsList: rawObjectsState.objectsList || [],
    showUpdateForm: rawObjectsState.showUpdateForm || false,
  };

  const session = useSelector((state) => state.session);
  console.log("session", session);
  const [viewModalShow, setViewModalShow] = useState(false);
  const [deleteModalShow, setDeleteModalShow] = useState(false);
  const [deleteRowId, setDeleteRowId] = useState(null);
  const [filterValue, setFilterValue] = useState("");
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [object_viewed_id, setObjectViewedId] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);

  useEffect(() => {
    setObjectViewedId(session?.current_page_permissions?.object_id || "");
  }, [session]);

  useEffect(() => {
    const id = session?.current_page_permissions?.object_id || "";
    dispatch(objectsListFetch(id));
  }, [dispatch, session]);

  useEffect(() => {
    setData(objectsState.objectsList);
    setFilteredData(objectsState.objectsList);
  }, [objectsState.objectsList]);

  useEffect(() => {
    const filtered = data.filter((row) =>
      Object.values(row).some(
        (value) =>
          value != null &&
          value.toString().toLowerCase().includes(filterValue.toLowerCase())
      )
    );
    setFilteredData(filtered);
  }, [filterValue, data]);

  const handleFilterChange = (e) => {
    setFilterValue(e.target.value);
    setCurrentPage(1);
  };

  const getDesc = (val) => trimAndLimit(val, 30) || "";

  const renderTooltip = (description) => <Tooltip>{description}</Tooltip>;

  const showViewPopup = (recordId) => {
    setViewModalShow(true);
    dispatch(getObjectsById(recordId, "view"));
  };

  // Open edit modal and fetch record
  const showUpdateForm = (recordId) => {
    dispatch(getObjectsById(recordId, "update"));
    setShowEditModal(true);
  };

  const handleDelete = (recordId) => {
    setDeleteRowId(recordId);
    setDeleteModalShow(true);
  };

  const totalPages = Math.ceil(filteredData.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const currentData = filteredData.slice(startIndex, startIndex + rowsPerPage);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  return (
    <div className="main-content-div page-card">
      <TableSearchField
        value={filterValue}
        onChange={handleFilterChange}
        className="mb-3"
      />

      {/* Table */}
      <Table responsive>
        <thead>
          <tr>
            <th>Parent</th>
            <th>Name</th>
            <th>Object Type</th>
            <th>URL</th>
            <th>Menu Order</th>
            <th>Status</th>
            <th>Description</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {currentData.length === 0 ? (
            <tr>
              <td colSpan={8}>
                <div className="empty-state py-4" role="status">
                  <img src="/assets/images/emptyStateTable.png" alt="" className="empty-state__image" aria-hidden="true" />
                  <p className="empty-state__title">No Records Found</p>
                  <p className="empty-state__message">There is nothing to display yet. Try adjusting your search.</p>
                </div>
              </td>
            </tr>
          ) : (
          currentData.map((row) => (
            <tr key={row.id}>
              <td>{row.parent_objects?.name || "N/A"}</td>
              <td>{row.name}</td>
              <td>{row.object_type?.opt_display || "N/A"}</td>
              <td>{row.object_url}</td>
              <td>{row.menu_order}</td>
              <td>{toggleStatus(row.is_active)}</td>
              <td>
                <OverlayTrigger
                  placement="top"
                  overlay={renderTooltip(row.description)}
                >
                  <span>{getDesc(row.description)}</span>
                </OverlayTrigger>
              </td>
              <td>
                <Image
                  src="/assets/images/view-icon.svg"
                  width={20}
                  height={20}
                  onClick={() => showViewPopup(row.id)}
                  style={{ cursor: "pointer" }}
                  className="me-2"
                />
                {AccessRights(
                  session?.current_page_permissions,
                  "edit",
                  row.created_by
                ) &&
                  row.is_deleted === "n" && (
                    <Image
                      src="/assets/images/edit-icon.svg"
                      width={20}
                      height={20}
                      onClick={() => showUpdateForm(row.id)}
                      style={{ cursor: "pointer" }}
                      className="me-2"
                    />
                  )}
                {AccessRights(
                  session?.current_page_permissions,
                  "delete",
                  row.created_by
                ) &&
                  row.is_deleted === "n" && (
                    <Image
                      src="/assets/images/delete-icon.svg"
                      width={20}
                      height={20}
                      onClick={() => handleDelete(row.id)}
                      style={{ cursor: "pointer" }}
                    />
                  )}
              </td>
            </tr>
          )))}
        </tbody>
      </Table>

      <TablePagination
        currentPage={currentPage}
        totalPages={totalPages}
        pageSize={rowsPerPage}
        pageSizeOptions={[5, 10, 20, 50]}
        onPageChange={handlePageChange}
        onPageSizeChange={(size) => {
          setRowsPerPage(size);
          setCurrentPage(1);
        }}
      />

      {/* Modals */}
      <ObjectUpdate
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
      />
      <ObjectViewModal
        show={viewModalShow}
        onHide={() => setViewModalShow(false)}
      />
      <DeleteModal
        show={deleteModalShow}
        onHide={() => setDeleteModalShow(false)}
        onDelete={() => {
          dispatch(deleteObjects(deleteRowId, object_viewed_id));
          setDeleteModalShow(false);
          dispatch(objectsListFetch(object_viewed_id));
        }}
      />
    </div>
  );
};

export default ObjectList;
